import * as React from 'react'
import { compose, Query } from 'react-apollo'
import { FormattedMessage, injectIntl } from 'react-intl'
import { RouteComponentProps } from 'react-router'
import Markdown from 'react-markdown'
import { get } from 'lodash'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import Notification from 'web-makeup/dist/Notification'
import QueryWithSkeleton from 'web-makeup/dist/QueryWithSkeleton'
import RequestItem from 'web-makeup/dist/RequestItem'
import { getCurrentUser } from 'web-fns/dist/auth'

import {
  GetComments,
  RequestTicket,
  RequestTicketAssigneeType,
  RequestTicketState,
  RequestTicketUrgency,
} from '../../typings/types'
import { isModerator } from '../../helpers/role-helper'
import BrandContactDetails from '../BrandContactDetails/BrandContactDetails'
import RequestCommentsContainer from '../RequestCommentsContainer'
import ModerationPanel from '../RequestDetailsPage/ModerationPanel'
import RequestStateTag from '../RequestDetailsPage/RequestStateTag'

import {
  DetailsHeader,
  MainContainer,
  RequestDetailsContainer,
  BrandMessageContainer,
  Entity,
  MainDescription,
  StyledH3,
  RequestType,
  RequestMessage,
  ProducerDetails,
  SectionTitle,
  Producer,
  AssigneeInfo,
  AssigneeProp,
  StyledIcon,
} from './RequestDetailsPage.style'
import { GET_COMMENTS } from './queries/get-comments'
import { GET_REQUEST_DETAILS } from './queries/get-request-details'

const IMAGE_SIZES = {
  mobile: {
    width: 64,
    height: 64,
  },
  tablet: {
    width: 64,
    height: 64,
  },
  desktop: {
    width: 64,
    height: 64,
  },
}

export const CommentsQuery = ({
  requestUuid,
  assigneeAvatar,
  requestState,
  unreadCommentsCount,
  assigneeUuid,
}) => (
  <Query<GetComments.Query> query={GET_COMMENTS} variables={{ uuid: requestUuid }}>
    {({ loading, data }) => {
      if (loading) {
        return null
      }

      const ticket = data && data.getRequestTicket
      const comments = (ticket && ticket.comments) || []
      return (
        <RequestCommentsContainer
          comments={comments}
          loading={loading}
          requestUuid={requestUuid}
          assigneeAvatar={assigneeAvatar}
          requestState={requestState}
          assigneeUuid={assigneeUuid}
          notifications={unreadCommentsCount}
        />
      )
    }}
  </Query>
)

interface RequestTicketResult {
  getRequestTicket: RequestTicket
}

type RequestDetailsComponentProps = {
  data: RequestTicketResult
  refetchFunc: any
} & ReactIntl.InjectedIntlProps

export const RequestDetailsComponent = ({
  data,
  refetchFunc,
  intl,
}: RequestDetailsComponentProps) => {
  if (!data || !data.getRequestTicket) {
    return (
      <span>
        <FormattedMessage id="request_details.get_request_detail.error_message" />
      </span>
    )
  }

  const currentUser = getCurrentUser()
  const requestTicket = data.getRequestTicket

  if (!requestTicket) {
    return null
  }

  const {
    requester,
    requestTicketType,
    assignee: assigneeInfo,
    uuid,
    state,
    unreadCommentsCount,
  } = requestTicket

  const assigneeAvatar = get(assigneeInfo, 'avatar')
  const assigneeUuid = get(assigneeInfo, 'uuid')
  const assigneeName = get(assigneeInfo, 'name')
  const assigneeLocation = get(assigneeInfo, 'location')
  const producerProfile = get(assigneeInfo, 'producerProfile')

  // The backend returns a JSON Blob so we have to parse it
  let ticketDetails
  try {
    ticketDetails = data.getRequestTicket.details && JSON.parse(data.getRequestTicket.details)
  } catch (err) {
    Notification.error({
      description: '',
      message: intl.formatMessage({
        id: 'request_details.description.parsing.ERROR',
      }),
    })
  }

  return (
    <>
      <DetailsHeader>
        <RequestItem
          ticket={requestTicket}
          itemStrings={{
            countdownSubtextMatching: intl.formatMessage({
              id: 'request_list_item.matching.label',
            }),
            countdownSubtextRemaining: intl.formatMessage({
              id: 'request_list_item.remaining_time.label',
            }),
            urgentText: '',
            asapText: '',
            preferredText: '',
            nonUrgentText: '',
            requestTypeTitle: requestTicket.requestTicketType.title,
          }}
          currentUserUuid={(currentUser && currentUser.uuid) || ''}
        />
        {uuid.length > 0 && isModerator() && <RequestStateTag state={requestTicket.state} />}
      </DetailsHeader>
      <MainContainer data-qa="request-details-page">
        <RequestDetailsContainer>
          {requester && <BrandContactDetails requester={requester} />}
          <>
            <BrandMessageContainer>
              <Entity>
                <CloudinaryImage
                  source={{
                    id: `icons/request-types/${requestTicketType.requestType}.png`,
                    type: 'upload',
                  }}
                  sizes={IMAGE_SIZES}
                  rounded
                />

                <MainDescription>
                  <StyledH3>
                    <FormattedMessage id={`request_type.${requestTicketType.requestType}.label`} />
                  </StyledH3>
                  <RequestType>{requestTicketType.title}</RequestType>
                </MainDescription>
              </Entity>

              {ticketDetails &&
                ticketDetails.description && (
                  <RequestMessage>
                    <StyledH3>
                      <FormattedMessage id="request_details.request_message.title" />
                    </StyledH3>

                    <Markdown
                      source={ticketDetails.description}
                      escapeHtml={true}
                      linkTarget="_blank"
                    />
                  </RequestMessage>
                )}
              {assigneeAvatar && (
                <ProducerDetails>
                  <SectionTitle>Assigned Producer</SectionTitle>
                  <Producer>
                    <CloudinaryImage
                      source={
                        assigneeAvatar
                          ? {
                              id: assigneeAvatar.cloudinaryId!,
                              type: assigneeAvatar.cloudinaryType!,
                            }
                          : {
                              id: 'placeholders/icon-user',
                              type: 'upload',
                            }
                      }
                      sizes={IMAGE_SIZES}
                      rounded
                    />
                    <AssigneeInfo>
                      <StyledH3>{assigneeName}</StyledH3>
                      {data.getRequestTicket.preferredAssigneeUuids.includes(assigneeUuid!) && (
                        <AssigneeProp>
                          <StyledIcon type="star" color="#F4A736" />
                          Preferred producer
                        </AssigneeProp>
                      )}
                      {data.getRequestTicket.workedBeforeAssigneeUuids.includes(assigneeUuid!) && (
                        <AssigneeProp>
                          <StyledIcon type="like" color="#bababa" />
                          Has worked with brand before
                        </AssigneeProp>
                      )}
                      <AssigneeProp>
                        <StyledIcon type="environment" color="#bababa" />
                        {assigneeLocation &&
                          `${assigneeLocation.city}, ${assigneeLocation.country}`}
                      </AssigneeProp>
                      <AssigneeProp>
                        <CloudinaryImage
                          source={
                            producerProfile
                              ? {
                                  id: producerProfile.serviceQualityLevel!.icon!.cloudinaryId!,
                                  type: producerProfile.serviceQualityLevel!.icon!.cloudinaryType!,
                                }
                              : {
                                  id: 'placeholders/icon-user',
                                  type: 'upload',
                                }
                          }
                          sizes={{
                            desktop: { width: 20, height: 20 },
                          }}
                          rounded
                        />
                        {producerProfile && producerProfile.serviceQualityLevel!.name}
                      </AssigneeProp>
                    </AssigneeInfo>
                  </Producer>
                </ProducerDetails>
              )}

              <ModerationPanel
                requestTicket={requestTicket}
                refetchFunc={refetchFunc}
                user={currentUser}
              />
            </BrandMessageContainer>
          </>
        </RequestDetailsContainer>

        {uuid && (
          <CommentsQuery
            requestUuid={uuid}
            assigneeAvatar={assigneeAvatar}
            requestState={state}
            unreadCommentsCount={unreadCommentsCount}
            assigneeUuid={assigneeUuid}
          />
        )}
      </MainContainer>
    </>
  )
}

const dummyData = {
  data: {
    getRequestTicket: {
      state: RequestTicketState.UNASSIGNED,
      urgency: RequestTicketUrgency.URGENT,
      assigneeType: RequestTicketAssigneeType.PRODUCER,
      unreadCommentsCount: 0,
      assignedAt: '2018-04-04T05:04:39Z',
      createdAt: '2018-04-04T05:05:39Z',
      details:
        '{"description":"Some sample description of a sample request ticket here. Please help!"}',
      preferredAssigneeEmails: null,
      reassignAt: '2018-04-04T05:09:39Z',
      requestTicketType: {
        category: 'category_4',
        requestType: 'something_else',
        subcategory: 'subcategory_4',
        subtext: 'Quia minima vel voluptatem provident in dolorem cumque id.',
        title: 'Dolorem aut enim praesentium optio.',
        uuid: '9df80c07-4cb4-4a16-a63a-9d60d15ec295',
      },
      resolvedAt: null,
      scheduledFor: null,
      comments: [],
      uuid: '',
      assignee: null,
    },
  },
  loading: true,
}

interface Props {
  data: RequestTicketResult
}

export const RequestDetailsPage: React.SFC<
  Props & ReactIntl.InjectedIntlProps & RouteComponentProps<{ uuid: string }>
> = props => (
  <QueryWithSkeleton
    query={GET_REQUEST_DETAILS}
    variables={{ uuid: props.match.params.uuid }}
    dummyData={dummyData}
    {...props}
  >
    {({ data, refetch }) => (
      <RequestDetailsComponent data={data} refetchFunc={refetch} intl={props.intl} />
    )}
  </QueryWithSkeleton>
)

export default compose(injectIntl)(RequestDetailsPage)



// WEBPACK FOOTER //
// ./src/components/RequestDetailsPage/RequestDetailsPage.tsx